import type { FC, ReactNode } from 'react';

import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';
import { Skeleton, TextSkeleton } from '@wwe-portal/ui/components/skeleton';
import { Each } from '@wwe-portal/ui/components/utils';

import { BalanceItem } from './balance-item';

import { InfoTooltip } from '../../components/tooltip/info-tooltip';
import { tryFormatThousands } from '@wwe-portal/utils/format';

export interface BalanceListProps {
  webCoin: number;
  ruby: number;
  energy: number;
  maxEnergy: number;
  pickem: number;
  clubCoin: number;

  addWebCoinSlot: ReactNode;
  addRubySlot: ReactNode;
  addMaxEnergySlot: ReactNode;
  addPickemSlot: ReactNode;
  addClubCoinSlot: ReactNode;
}

export const BalanceList: FC<BalanceListProps> = ({
  webCoin,
  ruby,
  energy,
  maxEnergy,
  pickem,
  clubCoin,
  addWebCoinSlot,
  addRubySlot,
  addMaxEnergySlot,
  addPickemSlot,
  addClubCoinSlot,
}) => {
  return (
    <div className="flex flex-col gap-1">
      <BalanceItem
        actionSlot={addWebCoinSlot}
        amountSlot={tryFormatThousands(webCoin)}
        currencySlot="Web Coins"
        iconSlot={
          <IcoMoonIcon
            className="text-blue700 size-4"
            iconName="web-coin-circle"
          />
        }
      />
      <BalanceItem
        actionSlot={addRubySlot}
        amountSlot={tryFormatThousands(ruby)}
        currencySlot="Rubies"
        iconSlot={
          <IcoMoonIcon
            className="text-purple600 size-4"
            iconName="diamond-fill"
          />
        }
      />
      <BalanceItem
        actionSlot={addMaxEnergySlot}
        amountSlot={
          <>
            {energy}/{maxEnergy}
          </>
        }
        currencySlot="Energy"
        iconSlot={
          <IcoMoonIcon
            className="text-yellow800 size-4"
            iconName="battery-charging"
          />
        }
      />
      <BalanceItem
        actionSlot={addPickemSlot}
        amountSlot={tryFormatThousands(pickem)}
        currencySlot={
          <div className="flex items-center gap-1">
            Pickem
            <InfoTooltip
              titleClassName="w-3 h-3"
              iconClassName="w-1.5 h-1.5 text-white"
              content={
                <>
                  <strong>How to use</strong>
                  <br />
                  During the Grand Tournament, use Pick-em&apos;s to pick your
                  favorite player to win the battle.
                </>
              }
              hasPortal={false}
            />
          </div>
        }
        iconSlot={
          <IcoMoonIcon className="size-4 text-[#23e996]" iconName="pickem" />
        }
      />
      <BalanceItem
        actionSlot={addClubCoinSlot}
        amountSlot={tryFormatThousands(clubCoin)}
        currencySlot="Club Coins"
        iconSlot={
          <IcoMoonIcon className="text-green700 size-4" iconName="club-coin" />
        }
      />
    </div>
  );
};

export const BalanceListSkeleton = () => {
  return (
    <div className="flex flex-col gap-1">
      <Each
        data={new Array(5).fill(0)}
        render={(_, index) => (
          <BalanceItem
            key={index}
            actionSlot={<Skeleton className="size-[26px]" />}
            amountSlot={
              <TextSkeleton className="w-20" fontSize={14} lineHeight={18} />
            }
            currencySlot={
              <TextSkeleton className="w-12" fontSize={10} lineHeight={25} />
            }
            iconSlot={<Skeleton className="size-4 rounded-full" />}
          />
        )}
      />
    </div>
  );
};
